import { useState } from 'react';
import { toast } from 'sonner';

import { Icons } from '@/assets/icons/icons';
import { Button } from '@/components/ui/Button';
import { Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/Input';
import { XStack, YStack } from '@/components/ui/layout/layout';
import { Loader } from '@/components/ui/Loader';
import { electronApis } from '@/features/electron-api';
import { ChromeCredentialImportButton } from '@/features/site-credentials/chrome-credential-import-button';
import {
  useDeleteSiteCredentialMutation,
  useSiteCredentialsQuery,
} from '@/features/site-credentials/site-credentials.api';
import type { SiteCredential } from '@/features/site-credentials/site-credentials.types';

import { SettingsSection } from '../settings-section';

import { useSiteCredentialEditForm } from './settings-browser-credentials.hooks';

export const SavedCredentialsGroup = () => {
  const credentials = useSiteCredentialsQuery();
  const deleteCredential = useDeleteSiteCredentialMutation();
  const [editedCredential, setEditedCredential] = useState<SiteCredential>();
  const [removeError, setRemoveError] = useState<string>();

  const removeCredential = (credential: SiteCredential) => {
    setRemoveError(undefined);
    deleteCredential.mutate(credential, {
      onSuccess: () => toast.success('Credential removed'),
      onError: () =>
        setRemoveError('Credential could not be removed. Try again.'),
    });
  };

  return (
    <SettingsSection.Group
      title="Saved credentials"
      trailing={
        electronApis.chromeImport.available ? (
          <ChromeCredentialImportButton />
        ) : null
      }
    >
      {credentials.isLoading ? (
        <XStack className="items-center gap-3 text-sm text-foreground-muted">
          <YStack className="size-5">
            <Loader />
          </YStack>
          <p>Loading saved credentials…</p>
        </XStack>
      ) : null}
      {credentials.isError ? (
        <YStack className="items-start gap-3">
          <p role="alert" className="text-sm text-danger-11">
            Saved credentials could not be loaded.
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => void credentials.refetch()}
          >
            Try again
          </Button>
        </YStack>
      ) : null}
      {credentials.data?.credentials.length === 0 ? (
        <p className="text-sm text-foreground-muted">
          No saved credentials yet.
        </p>
      ) : null}
      {credentials.data?.credentials.map((credential, index) => (
        <CredentialRow
          key={`${credential.domain}\n${credential.credential_id}`}
          credential={credential}
          separated={index > 0}
          busy={deleteCredential.isPending}
          onEdit={() => setEditedCredential(credential)}
          onRemove={() => removeCredential(credential)}
        />
      ))}
      {removeError ? (
        <p role="alert" className="text-sm text-danger-11">
          {removeError}
        </p>
      ) : null}
      {editedCredential ? (
        <EditCredentialDialog
          credential={editedCredential}
          onClose={() => setEditedCredential(undefined)}
        />
      ) : null}
    </SettingsSection.Group>
  );
};

const CredentialRow = ({
  credential,
  separated,
  busy,
  onEdit,
  onRemove,
}: {
  credential: SiteCredential;
  separated: boolean;
  busy: boolean;
  onEdit: () => void;
  onRemove: () => void;
}) => {
  const [confirmRemove, setConfirmRemove] = useState(false);
  return (
    <YStack className="gap-5">
      {separated ? <SettingsSection.Seperator /> : null}
      <SettingsSection.Item className="w-full min-h-8 flex-col items-stretch gap-4 sm:flex-row sm:items-center sm:justify-between">
        <YStack className="min-w-0 break-words">
          <SettingsSection.ItemHeader>
            <SettingsSection.ItemLabel>
              {credential.domain}
            </SettingsSection.ItemLabel>
            <SettingsSection.ItemDescription>
              {credential.credential_id}
            </SettingsSection.ItemDescription>
          </SettingsSection.ItemHeader>
        </YStack>
        <XStack className="flex-wrap gap-2 sm:shrink-0 sm:justify-end">
          <Button
            variant="ghost"
            size="icon"
            iconOnly
            aria-label={`Edit ${credential.credential_id} on ${credential.domain}`}
            title="Edit"
            disabled={busy}
            onClick={onEdit}
          >
            <Icons.pencil />
          </Button>
          <Button
            variant="destructive-ghost"
            size="icon"
            iconOnly
            aria-label={`Remove ${credential.credential_id} on ${credential.domain}`}
            title="Remove"
            disabled={busy}
            onClick={() => setConfirmRemove(true)}
          >
            <Icons.trash2 />
          </Button>
        </XStack>
      </SettingsSection.Item>
      <Dialog.Root open={confirmRemove} onOpenChange={setConfirmRemove}>
        <Dialog.Content className="max-w-sm">
          <Dialog.Header>
            <Dialog.Title>Remove saved credential?</Dialog.Title>
            <Dialog.Description>
              Littlebird will no longer be able to sign in to{' '}
              {credential.domain} as {credential.credential_id} for you.
            </Dialog.Description>
          </Dialog.Header>
          <Dialog.Footer>
            <Button variant="secondary" onClick={() => setConfirmRemove(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={busy}
              onClick={() => {
                setConfirmRemove(false);
                onRemove();
              }}
            >
              Remove credential
            </Button>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog.Root>
    </YStack>
  );
};

const EditCredentialDialog = ({
  credential,
  onClose,
}: {
  credential: SiteCredential;
  onClose: () => void;
}) => {
  const form = useSiteCredentialEditForm(credential, onClose);
  return (
    <Dialog.Root open onOpenChange={(open) => !open && onClose()}>
      <Dialog.Content className="max-w-md">
        <form
          onSubmit={(event) => {
            event.preventDefault();
            form.save();
          }}
        >
          <YStack className="gap-5">
            <Dialog.Header>
              <Dialog.Title>Edit saved credential</Dialog.Title>
              <Dialog.Description>
                {credential.credential_id} on {credential.domain}. Leave a field
                blank to keep its saved value.
              </Dialog.Description>
            </Dialog.Header>
            {form.fieldNames.isLoading ? (
              <XStack className="items-center gap-3 text-sm text-foreground-muted">
                <YStack className="size-5">
                  <Loader />
                </YStack>
                <p>Loading fields…</p>
              </XStack>
            ) : null}
            {form.fieldNames.isError ? (
              <p role="alert" className="text-sm text-danger-11">
                Credential fields could not be loaded.
              </p>
            ) : null}
            {form.fieldNames.data?.field_names.map((fieldName) => (
              <Input
                key={fieldName}
                label={fieldName}
                type="password"
                autoComplete="off"
                placeholder="Unchanged"
                value={form.values[fieldName] ?? ''}
                disabled={form.isSaving}
                onChange={(event) =>
                  form.setFieldValue(fieldName, event.target.value)
                }
              />
            ))}
            {form.saveError ? (
              <p role="alert" className="text-sm text-danger-11">
                {form.saveError}
              </p>
            ) : null}
            <Dialog.Footer>
              <Button type="button" variant="secondary" onClick={onClose}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={!form.hasChanges}
                isLoading={form.isSaving}
                loadingText="Saving…"
              >
                Save
              </Button>
            </Dialog.Footer>
          </YStack>
        </form>
      </Dialog.Content>
    </Dialog.Root>
  );
};
